import { Dispatch } from 'redux';
import { updateAppVersion } from '../stores/actions/session';
import { ShotData } from '../stores/reducers/shots';
import { MediaItem } from '../stores/reducers/media';
import { DRAG_TYPE } from '../stores/reducers/user-interface';
import { appLogger } from '../utils/datadog';

interface SyncErrorResponse {
	response?: Response;
	errorMessage?: string;
}

export const getHeaders = (token: string = '', meetingId: number | string | null = null): HeadersInit => {
	const parts = window?.location?.toString()?.split('/') ?? [''];

	let usedToken = token;
	if (!usedToken || usedToken === '') {
		try {
			usedToken = JSON.parse(localStorage.getItem('token') ?? '{}')?.item;
		} catch (e: any) {
			appLogger.error('error parsing token', {}, e);
		}
	}

	const stringedMeeting = meetingId?.toString();
	const meetingCode = stringedMeeting || parts[parts.length - 1]?.split(/[?#]/)[0];
	return {
		Accept: 'application/json',
		'Content-Type': 'application/json',
		'BL-TOKEN': token,
		'X-IS-PRODUCER': typeof window !== 'undefined' && window?.location?.pathname?.includes('producer')
			? 'true'
			: 'false',
		'X-MEETING': meetingCode !== 'producer'
			? meetingCode
			: '',
	};
};

export const getSyncHeaders = (token: string): HeadersInit => ({
	Accept: 'application/json',
	'Content-Type': 'application/json',
	'bl-token': token,
});

export const FetchFeatureFlags = async () => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/feature-flags`, {
	method: 'POST',
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json',
	},
}).then(async (result) => {
	if (result.ok) {
		const featureFlags = await result.json();
		return featureFlags;
	}
	appLogger.error('Unable to get feature flags.');
	return {};
}).catch((error) => {
	appLogger.error('error fetching feature flags', {}, error);
});

export const FetchFeatureFlagOverrides = async (meeting:number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/feature-flag-overrides/meeting/${meeting}`, {
	method: 'GET',
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json',
	},
}).then(async (result) => {
	if (result.ok) {
		const overrides = await result.json();
		return overrides;
	}
	appLogger.error('Unable to get feature flag overrides.');
	return {};
}).catch((error) => {
	appLogger.error('error fetching feature flag overrides', {}, error);
});

export const buildSyncUrl = (route: string) => {
	const { REACT_APP_SYNC_HOST = '' } = process.env;

	if (!REACT_APP_SYNC_HOST) {
		appLogger.error('Missing `REACT_APP_SYNC_HOST`.');
	}

	const syncHost = REACT_APP_SYNC_HOST.replace(/\/$/, '');
	const buildRoute = route.replace(/^\//, '');

	return `${syncHost}/${buildRoute}`;
};

export const handleResponseError = async ({
	response,
	errorMessage = 'unknown error',
}: SyncErrorResponse): Promise<null> => {
	let errorDetails: string = errorMessage;

	if (response) {
		errorDetails = await response.text();
	}

	appLogger.error(errorDetails);

	return null;
};

export const getCurrentVersion = async (token: string, dispatch: Dispatch) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/current-version`, {
	method: 'GET',
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json',
		'bl-token': token,
	},
}).then(async (result) => {
	if (result.ok) {
		const data = await result.json();
		if (data.version) {
			return dispatch(updateAppVersion(data?.version));
		}
	}
	appLogger.error('Unable to request current version.');
	return null;
}).catch((error) => {
	appLogger.error('getCurrentVersion error', {}, error);
});

export const getValidShotData = async (shotData: ShotData, shotMedia: MediaItem[]) => {
	try {
		const response = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/shots/validate-shot`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ shotData, shotMedia }),
		});
		if (response.ok) {
			return response.json();
		}
		appLogger.error('could not validate shot');
		return null;
	} catch (error: any) {
		appLogger.error('error validating shot', {}, error);
		return null;
	}
};

export const validateShotData = async (shotData: ShotData, media: MediaItem[]) => {
	const shotMedia: MediaItem[] = [];
	shotData.shotFeeds.forEach((feed) => {
		if (feed.feedType === DRAG_TYPE.FEED_MEDIA) {
			const item = media.find((m: MediaItem) => m.id === feed.media);
			if (item) {
				shotMedia.push(item);
			}
		}
	});
	const validatedShotData = await getValidShotData(shotData, shotMedia);
	if (validatedShotData) {
		return validatedShotData;
	}
	return shotData;
};
